import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';

import { CardContent } from '@/shared/components/card-content/card-content';
import { TableModule } from 'primeng/table';
import { OrgaoResponse } from '@/core/api/sicom-api/registro/orgao/orgao.model';
import { DadosGerais } from '@/features/registro/orgaos/dashboard-orgao/components/dados-gerais/dados-gerais';
import { DashboardOrgaoService } from '@/features/registro/orgaos/dashboard-orgao/dashboard-orgao.service';
import { Contato } from '@/shared/components/contato/contato';
import { Composicao } from '@/features/registro/orgaos/dashboard-orgao/components/composicao/composicao';
import { Delegacias } from '@/features/registro/orgaos/dashboard-orgao/components/delegacias/delegacias';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

/**
 * Componente para gerenciar o cadastro de Órgãos e Delegacias.
 * Permite criar, editar e visualizar informações de órgãos do sistema.
 */
@Component({
    selector: 'app-dashboard-orgao',
    standalone: true,
    imports: [CardContent, FormsModule, ReactiveFormsModule, InputTextModule, ButtonModule, TextareaModule, CheckboxModule, DatePickerModule, SelectButtonModule, TableModule, DadosGerais, Contato, Composicao, Delegacias, CanDirective],
    templateUrl: './dashboard-orgao.html',
    styleUrl: './dashboard-orgao.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardOrgao implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly dashboardOrgaoService = inject(DashboardOrgaoService);
    private readonly pessoaService = inject(PessoaService);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly PERMISSOES = PERMISSOES;

    orgao: OrgaoResponse | null = null;
    contextoSolicitanteOrgao: Pessoa | null = null;
    isLoading = false;
    abaAtiva = '0';

    private _uf = 'DF';

    get uf(): string {
        return this._uf;
    }

    set uf(value: string) {
        if (!value || value === this._uf) {
            return;
        }

        this._uf = value;
        this.carregarOrgao();
    }

    ngOnInit(): void {
        this.definirPessoaContatoFallback();
        this.carregarOrgao();
    }

    recarregarDadosGeraisOrgao(): void {
        this.carregarOrgao();
    }

    private carregarOrgao(): void {
        this.isLoading = true;
        this.dashboardOrgaoService
            .buscarOrgao(this.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (orgao) => {
                    this.orgao = orgao;
                    this.definirPessoaContato(orgao);
                    this.isLoading = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.orgao = null;
                    this.definirPessoaContatoFallback();
                    this.isLoading = false;
                    this.cdr.markForCheck();
                }
            });
    }

    private definirPessoaContato(orgao: OrgaoResponse): void {
        const idSolicitante = Number(orgao.idSolicitante ?? orgao.id ?? 0);
        if (idSolicitante > 0) {
            this.contextoSolicitanteOrgao = { id: idSolicitante, uf: this.uf };
            return;
        }

        this.definirPessoaContatoFallback();
    }

    private definirPessoaContatoFallback(): void {
        const pessoaSessao = this.pessoaService.getPessoa() ?? this.pessoaService.getPessoaReference();
        if (pessoaSessao?.id) {
            this.contextoSolicitanteOrgao = { id: Number(pessoaSessao.id), uf: this.uf };
            return;
        }

        this.contextoSolicitanteOrgao = null;
    }
}
